import React from 'react';
import Table, { TableActionBar, TableCell, TableRow } from '@amzn/meridian/table';
import {
    buildGetContainerDetailsInput,
    GetContainerDetailsInput,
    GetContainerDetailsOutput,
    GetContainerMode
} from 'src/models/GetContainerDetails';
import { ContainerDetailsModel } from 'src/components/common/container-details/ContainerDetailsModel';
import { getContainerIdString } from 'src/data-map/containerDetailsToStringID';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Badge from '@amzn/meridian/badge';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import undoTokens from '@amzn/meridian-tokens/base/icon/undo';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import { SearchPages } from 'src/store/common-slices/searchSlice';
import { getChildContainerTypeForParent } from 'src/utils/container-details-util';

interface ChildContainersPageStateProps {
    containerDetails?: GetContainerDetailsOutput;
    warehouseId: string;
    associate: string;
    locale: string;
}

interface ChildContainersPageDispatchProps {
    fetchContainerDetails: (getContainerDetailsInput: GetContainerDetailsInput) => void;
    setPageToRender: (pageToRender: SearchPages) => void;
}

interface ChildContainersPageState {
    expandedRows: string[]
}

type Props = ChildContainersPageStateProps & ChildContainersPageDispatchProps;

/*
    This component is to render the details of child containers.
    For example, if the user searched for a PALLET, on the first screen there'll be option to View TOTEs inside that pallet.
    If user opts to View totes, this component renders the list of all the totes inside pallet.
*/
class ChildContainersPage extends React.Component<Props, ChildContainersPageState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            expandedRows: []
        }
    }

    private onExpandClick = (container: ContainerDetailsModel) => {
        if (this.state.expandedRows.includes(container.containerId)) {
            this.setState({
                expandedRows: this.state.expandedRows.filter(id => id!==container.containerId)
            })
        } else {
            this.setState({
                expandedRows: [...this.state.expandedRows, container.containerId]
            })
        }
    }

    // If the user clicks on back icon, they should be taken back to the page with details of searched container.
    private onBackClick = async () => {
        await this.props.setPageToRender(SearchPages.SEARCHED_CONTAINER);
    }

    /*
        If the user clicks on any of the containers from the list,
        we should fetch and render the details of that container.
    */
    private onContainerIdClick = async (container: ContainerDetailsModel) => {
        const containerId: string = container.containerId;
        await this.props.fetchContainerDetails(buildGetContainerDetailsInput(containerId, this.props.warehouseId,
            this.props.associate, this.props.locale, GetContainerMode.SEARCH, true));
        await this.props.setPageToRender(SearchPages.SEARCHED_CONTAINER);
    }

    /* This is to render the action bar on top.
       It includes back icon, container type and id, number of child containers
    */
    private getTableActionBar = (container: ContainerDetailsModel) => {
        return(
            <TableActionBar>
                <Button type={"secondary"} size="small" onClick={this.onBackClick}><Icon tokens={undoTokens}/></Button>
                <Text type={"h50"}>{container.containerType}:</Text>
                <Text type={"h50"}>{container.containerId}</Text>
                <Text type={"h50"}>#:{container.numOfChildContainers}</Text>
            </TableActionBar>
        );
    }

    /* This is to render the header for child containers. It will look like:
       `LPN label   CDD`
    */
    private getTableHeader = (containers: ContainerDetailsModel[]) => {
        const containerType = containers.find(container => container.containerType !== undefined)?.containerType;
        return(
            <TableRow>
                <TableCell>{getContainerIdString(containerType)}</TableCell>
                <TableCell>{AppStrings.SEARCH_TABLE_CDD}</TableCell>
            </TableRow>
        )
    }

    private getTableRows = (containers: ContainerDetailsModel[]) => {
        return containers.map((container, counter) => {
            const isRowOpen = this.state.expandedRows.some(containerId => containerId===container.containerId);
            return(
                <TableRow key={counter} open={isRowOpen}>
                    <TableCell header={true}>
                        <Button type={"icon"} onClick={() => this.onExpandClick(container)}>
                            <Icon tokens={isRowOpen ? chevronDownSmallTokens : chevronRightSmallTokens}/>
                        </Button>
                        <Badge value={counter}/>
                        <Link onClick={() => this.onContainerIdClick(container)}>
                            {container.containerId}
                        </Link>
                    </TableCell>
                    <TableCell header={true}>{container.containerProperties?.cleanDecantDate}</TableCell>
                    {this.getExpandableRows(container)}
                </TableRow>
            )
        })
    }

    private onViewItems = async (container: ContainerDetailsModel) => {
        await this.props.fetchContainerDetails(buildGetContainerDetailsInput(container.containerId,
            this.props.warehouseId, this.props.associate, this.props.locale, GetContainerMode.SEARCH, true));
    }

    private properties = [
        { key: 'sortationCategories', label: AppStrings.SEARCH_TABLE_SORTATION_CATEGORY },
        { key: 'latestProcessingDate', label: AppStrings.SEARCH_TABLE_LPD }
    ];

    /*
        This returns the rows that need to be rendered in case the container row is expanded.
    */
    private getExpandableRows = (container: ContainerDetailsModel) => {
        const rows: React.ReactElement[] = [];

        const childContainerType = getChildContainerTypeForParent(container.containerType);

        // First row in expanded rows would be of child containers - type and count
        if (childContainerType) {
            rows.push(
                <TableRow backgroundColor="secondary">
                    <TableCell>{childContainerType}</TableCell>
                    <TableCell header={true}>
                        {container.numOfChildContainers}:
                        <Link onClick={() => this.onViewItems(container)}>
                            {AppStrings.SEARCH_TABLE_VIEW_ITEMS} {childContainerType}
                        </Link>
                    </TableCell>
                </TableRow>
            )
        }

        // remaining rows would be of other properties like sortation category, etc.
        this.properties.map(({key, label}) => {
            const keys = key.split('.');
            const value = keys.reduce((acc: any, curr) => acc?.[curr], container);
            if (value) {
                rows.push(
                    <TableRow backgroundColor="secondary">
                        <TableCell>{label}</TableCell>
                        <TableCell header={true}>{value}</TableCell>
                    </TableRow>
                )
            }
            return undefined;
        })
        return rows;
    }

    public render() {
        return (
            <Table showDividers={true} showExpandedDividers={true} headerRows={1} data-testid="search-child-containers-table-scanner">
                {this.props.containerDetails?.container && this.getTableActionBar(this.props.containerDetails.container)}
                {this.props.containerDetails?.childContainers && this.getTableHeader(this.props.containerDetails.childContainers)}
                {this.props.containerDetails?.childContainers && this.getTableRows(this.props.containerDetails.childContainers)}
            </Table>
        );
    }
}

export default ChildContainersPage;
